import { mutate as mutateGlobal } from 'swr'
import type {
  AudienceCreationFlowSnapshot,
  AudienceImportRouteAck,
} from '@digital-twins/shared-types'
import type { AudienceImportThinRouteAck } from '@/types/components/audiences/import-session'
import { startImportSessionRouteTransition } from './import-session-client-metrics'
import {
  getAudienceImportContextMaterialsProjectionKey,
  getAudienceImportFlowSnapshotKey,
  getAudienceImportPreviewProjectionKey,
  getAudienceImportSessionKey,
  getAudienceImportStatusProjectionKey,
  getAudienceImportWorkbookProjectionKey,
} from './cache-keys'
import {
  mergeRouteAckIntoContextMaterialsProjection,
  mergeRouteAckIntoPreviewProjection,
  mergeRouteAckIntoSession,
  mergeRouteAckIntoStatusProjection,
  mergeRouteAckIntoWorkbookProjection,
  preferNewerFlowSnapshot,
} from './import-session-deltas'
import { stepKeyFromRoute } from './import-wizard-routes'

type RouteAckForWarm = AudienceImportRouteAck | AudienceImportThinRouteAck

/** Minimal router surface (replaces Next `AppRouterInstance` after Next removal). */
export type ImportSessionRouter = Pick<{ push: (href: string) => void }, 'push'>

function normalizeRoute(route: string | null | undefined) {
  return route?.trim() ?? ''
}

function firstNonEmptyRoute(routes: Array<string | null | undefined>) {
  for (const route of routes) {
    const normalized = normalizeRoute(route)
    if (normalized) {
      return normalized
    }
  }
  return ''
}

export function deriveImportTargetRoute(args: {
  sessionId: string
  routeAck?: Pick<AudienceImportRouteAck, 'sessionId' | 'flowSnapshot'> | null
  snapshot?: Pick<AudienceCreationFlowSnapshot, 'route' | 'links'> | null
  fallbackRoutes?: Array<string | null | undefined>
  defaultRoute?: string | null | undefined
}) {
  const handoffSessionId = args.routeAck?.sessionId ?? args.sessionId
  return firstNonEmptyRoute([
    args.routeAck?.flowSnapshot?.route,
    ...(args.fallbackRoutes ?? []),
    args.snapshot?.route,
    args.defaultRoute ?? `/audiences/imports/${handoffSessionId}/status`,
  ])
}

/**
 * Resume target for "Restore last import" from Create audience.
 *
 * The canonical `snapshot.route` can still be `/…/status` while async dataset
 * preflight runs if a stale create run remains queued — backend wizard phase
 * prioritizes create state before preflight. When preflight is pending or the
 * wizard is in demographics review, prefer `links.requiredFields` (or context)
 * instead of the status URL.
 */
export function resolveImportResumeTargetRoute(args: {
  sessionId: string
  snapshot: AudienceCreationFlowSnapshot
  latestSessionRoute?: string | null
}): string {
  const { sessionId, snapshot } = args
  const base =
    normalizeRoute(snapshot.route) ||
    normalizeRoute(args.latestSessionRoute) ||
    deriveImportTargetRoute({
      sessionId,
      snapshot,
      defaultRoute: `/audiences/imports/${sessionId}/status`,
    })

  const statusLink = normalizeRoute(snapshot.links?.status)
  const requiredFields = normalizeRoute(snapshot.links?.requiredFields)
  const contextMaterials = normalizeRoute(snapshot.links?.contextMaterials)

  const baseStep = stepKeyFromRoute(base)
  const pointsAtStatus =
    baseStep === 'creating' || (Boolean(statusLink) && base === statusLink)

  if (!pointsAtStatus) {
    return base
  }

  if (snapshot.wizardPhase === 'context_materials' && contextMaterials) {
    return contextMaterials
  }

  if (
    snapshot.preflightStatus === 'pending' &&
    requiredFields
  ) {
    return requiredFields
  }

  if (snapshot.wizardPhase === 'identity_review' && requiredFields) {
    return requiredFields
  }

  return base
}

/**
 * Navigates to the target import route. Destination pages restore from the
 * canonical backend snapshot on mount, so navigation does not depend on
 * client-side cache warming.
 */
export function navigateToImportRoute(args: {
  sessionId: string
  sourceRoute: string
  targetRoute: string | null | undefined
  router: ImportSessionRouter
  trigger: string
  additionalRoutes?: Array<string | null | undefined>
}) {
  const targetRoute = normalizeRoute(args.targetRoute)
  if (!args.sessionId || !targetRoute || targetRoute === args.sourceRoute) {
    return false
  }

  startImportSessionRouteTransition({
    sessionId: args.sessionId,
    sourceRoute: args.sourceRoute,
    targetRoute,
    trigger: args.trigger,
  })
  args.router.push(targetRoute)
  return true
}

/**
 * After a mutation returns a routeAck, push updated state into SWR caches so the
 * next wizard step (or same step) sees fresh flow snapshot / projections without
 * waiting for refetch. No-op when `ack` is missing or has no flowSnapshot.
 */
export async function warmImportRouteCaches(args: {
  sessionId: string
  targetRoute?: string | null | undefined
  ack?: RouteAckForWarm | null
}): Promise<void> {
  const { sessionId, ack } = args
  if (!sessionId || !ack?.flowSnapshot) {
    return
  }

  const flowKey = getAudienceImportFlowSnapshotKey(sessionId)
  if (flowKey) {
    await mutateGlobal(
      flowKey,
      (cached: AudienceCreationFlowSnapshot | null | undefined) =>
        preferNewerFlowSnapshot(cached ?? null, ack.flowSnapshot),
      { revalidate: false },
    )
  }

  const warm = async (
    key: readonly unknown[] | null,
    merge: (current: unknown) => unknown,
  ) => {
    if (!key) return
    await mutateGlobal(
      key,
      (current: unknown) => {
        const next = merge(current)
        return next !== undefined ? next : current
      },
      { revalidate: false },
    )
  }

  await Promise.all([
    warm(getAudienceImportStatusProjectionKey(sessionId), (c) =>
      mergeRouteAckIntoStatusProjection(c as never, ack),
    ),
    warm(getAudienceImportPreviewProjectionKey(sessionId), (c) =>
      mergeRouteAckIntoPreviewProjection(c as never, ack),
    ),
    warm(getAudienceImportWorkbookProjectionKey(sessionId), (c) =>
      mergeRouteAckIntoWorkbookProjection(c as never, ack),
    ),
    warm(getAudienceImportContextMaterialsProjectionKey(sessionId), (c) =>
      mergeRouteAckIntoContextMaterialsProjection(c as never, ack),
    ),
    warm(getAudienceImportSessionKey(sessionId), (c) =>
      mergeRouteAckIntoSession(c as never, ack),
    ),
  ])
}

/* Pending create intent (sessionStorage handoff between steps) */

const CREATE_INTENT_STORAGE_PREFIX = 'audience-import:create-intent:'

function getCreateIntentStorageKey(sessionId: string) {
  return `${CREATE_INTENT_STORAGE_PREFIX}${sessionId}`
}

function parsePendingCreateIntent(raw: string | null): {
  activeFilterColumns: string[]
  customLabels: Record<string, string>
  audienceTags: unknown[]
} | null {
  if (!raw) {
    return null
  }

  try {
    const parsed = JSON.parse(raw) as {
      activeFilterColumns?: unknown
      customLabels?: unknown
      audienceTags?: unknown
    }
    return {
      activeFilterColumns: Array.isArray(parsed.activeFilterColumns)
        ? parsed.activeFilterColumns.filter(
            (value): value is string =>
              typeof value === 'string' && value.length > 0,
          )
        : [],
      customLabels:
        parsed.customLabels !== null &&
        typeof parsed.customLabels === 'object' &&
        !Array.isArray(parsed.customLabels)
          ? Object.fromEntries(
              Object.entries(parsed.customLabels as Record<string, unknown>)
                .filter(([, v]) => typeof v === 'string')
                .map(([k, v]) => [k, v as string]),
            )
          : {},
      audienceTags: Array.isArray(parsed.audienceTags)
        ? parsed.audienceTags
        : [],
    }
  } catch {
    return { activeFilterColumns: [], customLabels: {}, audienceTags: [] }
  }
}

export function persistPendingCreateIntent(args: {
  sessionId: string
  activeFilterColumns?: string[]
  customLabels?: Record<string, string>
  audienceTags?: unknown[]
}) {
  if (typeof window === 'undefined' || !args.sessionId) {
    return
  }

  window.sessionStorage.setItem(
    getCreateIntentStorageKey(args.sessionId),
    JSON.stringify({
      activeFilterColumns: args.activeFilterColumns ?? [],
      customLabels: args.customLabels ?? {},
      audienceTags: args.audienceTags ?? [],
    }),
  )
}

export function readPendingCreateIntent(sessionId: string): {
  activeFilterColumns: string[]
  customLabels: Record<string, string>
  audienceTags: unknown[]
} | null {
  if (typeof window === 'undefined' || !sessionId) {
    return null
  }

  return parsePendingCreateIntent(
    window.sessionStorage.getItem(getCreateIntentStorageKey(sessionId)),
  )
}

export function consumePendingCreateIntent(sessionId: string): {
  activeFilterColumns: string[]
  customLabels: Record<string, string>
  audienceTags: unknown[]
} | null {
  if (typeof window === 'undefined' || !sessionId) {
    return null
  }

  const storageKey = getCreateIntentStorageKey(sessionId)
  const raw = window.sessionStorage.getItem(storageKey)
  window.sessionStorage.removeItem(storageKey)

  return parsePendingCreateIntent(raw)
}

export function clearPendingCreateIntent(sessionId: string) {
  if (typeof window === 'undefined' || !sessionId) {
    return
  }

  window.sessionStorage.removeItem(getCreateIntentStorageKey(sessionId))
}
